/**
 * History page - View historical portfolio snapshots.
 */

import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { portfolioApi } from '../services/api';
import { usePortfolio } from '../contexts/PortfolioContext';

export function HistoryPage() {
  const { selectedPortfolioId, selectedPortfolio } = usePortfolio();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [snapshot, setSnapshot] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSnapshot = async (date: Date) => {
    if (!selectedPortfolioId) return;
    
    try {
      setLoading(true);
      setError(null);
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
      const data = await portfolioApi.getHistoryById(selectedPortfolioId, dateStr);
      setSnapshot(data.snapshot);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load snapshot');
      console.error('History load error:', err);
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    if (date) {
      loadSnapshot(date);
    }
  };

  useEffect(() => {
    if (selectedDate && selectedPortfolioId) {
      loadSnapshot(selectedDate);
    }
  }, [selectedPortfolioId]);

  return (
    <Box>
      {selectedPortfolio && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {selectedPortfolio.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedPortfolio.portfolioType} Portfolio
            </Typography>
          </CardContent>
        </Card>
      )}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Historical Portfolio Snapshots
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box display="flex" gap={2} alignItems="center" sx={{ mt: 2 }}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={handleDateChange}
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    size: 'small',
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={() => selectedDate && loadSnapshot(selectedDate)}
                disabled={!selectedDate || loading}
              >
                Load Snapshot
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {snapshot !== null && !loading && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Snapshot Data
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'grey.50', overflow: 'auto', maxHeight: 600 }}>
              <pre style={{ margin: 0, fontSize: '0.875rem', fontFamily: 'monospace' }}>
                {JSON.stringify(snapshot as Record<string, unknown>, null, 2)}
              </pre>
            </Paper>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

